import { isTeamAdmin } from "./team_utils"

export const computeGoogleCalendarConfigState = newConfigData => {
    const defaultState = {
        items: {},
        sort: [],
        selected: null,
        pendingConfigTeamId: null,
        saveInProgress: false,
    }

    return newConfigData.reduce((acc, element) => {
        const { sort, items } = acc
        const newItems = { ...items, [element.id]: element }
        const newSort = [...sort, element.id]
        return { ...acc, sort: newSort, items: newItems }
    }, defaultState)
}

export const computeNewCalendarConfigState = (oldState, newConfigData) => {
    const { sort, items } = computeGoogleCalendarConfigState(newConfigData)
    const { selected: oldSelected } = oldState
    const selected = sort.includes(oldSelected) ? oldSelected : null
    return { ...oldState, sort, items, selected }
}

export const ingressNewConfigToState = (oldState, config) => {
    const { sort: oldSort, items } = oldState
    const { id } = config
    const sort = oldSort.indexOf(id) != -1 ? oldSort : [...oldSort, id]
    return {
        ...oldState,
        sort,
        items: { ...items, [id]: { ...config } },
    }
}

export const deleteCalendarConfigFromState = (oldState, idToDelete) => {
    const { sort, items } = oldState.sort.reduce((acc, id) => {
        return id === idToDelete
            ? acc
            : { items: { ...acc.items, [id]: { ...oldState.items[id] } }, sort: [...acc.sort, id] }

    }, { items: {}, sort: [] })
    const selected = oldState.selected === idToDelete ? null : oldState.selected
    
    return { ...oldState, sort, items, selected }
}

export const setPendingConfigTeamId = (configState, teamId) => {
    return { ...configState, pendingConfigTeamId: teamId, selected: null }
}

export const setSelectedGoogleCalendarConfigId = (configState, id) => {
    return {
        ...configState,
        selected: id,
        pendingConfigTeamId: null,
    }
}

export const getTeamConfig = (teams, teamId) => {
    const { items } = teams
    const { [teamId]: team } = items
    return team
}

export const filterAvailableCalendars = availableCalendars => {
    return availableCalendars.filter(
        calendar =>
            !calendar.isSubscribed &&
            calendar.title != "Holidays in United States" &&
            calendar.title != "Contacts",
    )
}

export const teamHasConfig = (configState, teamId) => {
    const configIdWithMatchingTeam = configState.sort.find(configId => {
        return configState.items[configId].teamId === teamId
    })
    return !!configIdWithMatchingTeam
}

export const removeAccount = (state, configId, accountIdToDelete) => {
    const configToAlter = state.items[configId]
    if (!configToAlter) {
        return state
    }
    // calculate new accounts list, removing accountIdToDelete
    const { gCalendarAccounts } = configToAlter
    const newAccounts = gCalendarAccounts.filter(
        account => account.id !== accountIdToDelete,
    )
    // calculate new config with new accounts list
    const newConfig = { ...configToAlter, gCalendarAccounts: newAccounts }
    // calculate new state
    return {
        ...state,
        items: {
            ...state.items,
            [configId]: newConfig,
        },
    }
}

export const removeCalendarSubscription = (
    state,
    configId,
    calendarIdToDelete,
) => {
    const configToAlter = state.items[configId]
    if (!configToAlter) {
        return state
    }

    const { gCalendarSubscriptions } = configToAlter
    const newCalendars = gCalendarSubscriptions.filter(
        calendar => calendar.id !== calendarIdToDelete,
    )
    const calendarToDelete = gCalendarSubscriptions.find(
        calendar => calendar.id === calendarIdToDelete,
    )
    const availableCalendars = configToAlter.availableCalendars.map(cal => {
        if (cal.id === calendarToDelete.gCalendarId) {
            return {
                ...cal,
                isSubscribed: false,
            }
        }
        return cal
    })
    const newConfig = {
        ...configToAlter,
        gCalendarSubscriptions: newCalendars,
        availableCalendars,
    }

    return {
        ...state,
        items: {
            ...state.items,
            [configId]: newConfig,
        },
    }
}

// returns array of teams where the current user is the admin
// and the team does not already have a google calendar config
export const availableTeamsWithoutCalendarConfigs = (
    profileState,
    teamState,
    configState,
) => {
    return Object.values(teamState.items).filter(({ id }) => {
        const isAdmin = isTeamAdmin(id, profileState)
        const hasConfig = teamHasConfig(configState, id)

        return isAdmin && !hasConfig
    })
}

export const addNewAccountsToConfigState = (
    currentState,
    configId,
    gCalendarAccounts,
    availableCalendars,
) => {
    const selectedConfig = currentState.items[configId]
    const updatedConfig = {
        ...selectedConfig,
        gCalendarAccounts,
        availableCalendars,
    }
    const newState = {
        ...currentState,
        items: {
            ...currentState.items,
            [configId]: updatedConfig,
        },
    }

    return newState
}

export const setConfigDescription = (
    currentState,
    configId,
    defaultDescription,
) => {
    const selectedConfig = currentState.items[configId]
    const updatedConfig = {
        ...selectedConfig,
        defaultDescription,
    }
    const newState = {
        ...currentState,
        items: {
            ...currentState.items,
            [configId]: updatedConfig,
        },
    }
    return newState
}

export const setConfigCreateRecordsForMembers = (
    currentState,
    createRecordsForMembers,
    configId,
) => {
    return updateStateWithConfigField(
        currentState,
        "createRecordsForMembers",
        createRecordsForMembers,
        configId,
    )
}

export const setConfigCreateAllDayRecords = (
    currentState,
    createAllDayRecords,
    configId,
) => {
    return updateStateWithConfigField(
        currentState,
        "createAllDayRecords",
        createAllDayRecords,
        configId,
    )
}

export const updateTagKeywords = (currentState, tagKeywords, configId) => {
    const selectedConfig = currentState.items[configId]
    const updatedConfig = {
        ...selectedConfig,
        tagKeywords,
    }
    const newState = {
        ...currentState,
        items: {
            ...currentState.items,
            [configId]: updatedConfig,
        },
    }
    return newState
}

export const updateAttendeeUsers = (currentState, attendeeUsers, configId) => {
    return updateStateWithConfigField(
        currentState,
        "attendeeUsers",
        attendeeUsers,
        configId,
    )
}

export const updateStateWithConfigField = (
    currentState,
    field,
    fieldValue,
    configId,
) => {
    const selectedConfig = currentState.items[configId]
    const updatedConfig = {
        ...selectedConfig,
        [field]: fieldValue,
    }
    const newState = {
        ...currentState,
        items: {
            ...currentState.items,
            [configId]: updatedConfig,
        },
    }
    return newState
}

export const updateDefaultTags = (currentState, configId, defaultTags) => {
    const selectedConfig = currentState.items[configId]
    const updatedConfig = {
        ...selectedConfig,
        defaultTags,
    }
    const newState = {
        ...currentState,
        items: {
            ...currentState.items,
            [configId]: updatedConfig,
        },
    }
    return newState
}

export const sortMatches = (config, field, incomingMatches, field1, field2) => {
    const { id } = config
    const oldMatches = config[field]
    const configId = id
    const initial = { newMatches: [], matchesToUpdate: [] }
    // returns { newMatches, matchesToUpdate }
    const { newMatches, matchesToUpdate } = incomingMatches.reduce(
        (acc, item) => {
            // not enough info to post or update
            if (!item[field1] || !item[field2]) {
                return acc
            }
            const existingMatch = item.id
                ? oldMatches.find(t => (t.id === item.id))
                : null
            if (!existingMatch) {
                return {
                    ...acc,
                    newMatches: [...acc.newMatches, { ...item, configId }],
                }
            }
            const isUpdated =
                existingMatch[field1] != item[field1] ||
                existingMatch[field2] != item[field2]
            return isUpdated
                ? {
                    ...acc,
                    matchesToUpdate: [
                        ...acc.matchesToUpdate,
                        { ...item, configId },
                    ],
                } : acc
        },
        initial,
    )

    const remainingIds = incomingMatches.reduce((acc, item) => {
        return item.id ? [...acc, item.id] : acc
    }, [])
    const matchesToDelete = oldMatches
        ? oldMatches.reduce((acc, oldItem) => {
            // if old item ID is not in remaining ids, add it to item Ids to delete
            return remainingIds.indexOf(oldItem.id) === -1
                ? [...acc, oldItem.id]
                : acc
        }, []) : []
    return { newMatches, matchesToUpdate, matchesToDelete }
}

export const sortTagKeywords = (config, newTagKeywords) => {
    const { newMatches, matchesToUpdate, matchesToDelete } = sortMatches(
        config,
        "tagKeywords",
        newTagKeywords,
        "tagId",
        "keyword",
    )

    return {
        newKeywords: newMatches,
        updatedKeywords: matchesToUpdate,
        keywordIdsToDelete: matchesToDelete,
    }
}

export const sortAttendeeUsers = (config, newAttendeeUsers) => {
    const cleanData = newAttendeeUsers.map(i => {
        const { attendeeEmail, userId } = i
        return { userId, email: attendeeEmail, configId: config.id }
    })

    return sortMatches(config, "attendeeUsers", cleanData, "email", "userId")
}

export const putConfigMeta = (config, patchMeta) => {
    const {
        createAllDayRecords,
        createRecordsForMembers,
        defaultTags,
        defaultDescription,
        teamId,
    } = config

    const defaultTagIds = defaultTags.map(({ tagId }) => tagId)

    return {
        createAllDayRecords,
        createRecordsForMembers,
        defaultTagIds,
        defaultDescription,
        teamId,
        ...patchMeta,
    }
}

export const sortNewAndExistingTags = (tagsArray) => {
    return tagsArray.reduce((acc, item) => {
        if (item.newOption) {
            return { ...acc, newTags: [...acc.newTags, item] }
        }
        return { ...acc, existingTagIds: [...acc.existingTagIds, item.id] }
    }, { newTags: [], existingTagIds: [] })
}

export const sortNewAndExistingKeywordTags = (keywordTagsArray) => {
    return keywordTagsArray.reduce((acc, item) => {
        if (!item.tag) {
            return acc
        }
        if (item.tag.newOption) {
            return { ...acc, usesNewTag: [...acc.usesNewTag, item] }
        }
        return { ...acc, usesExistingTag: [...acc.usesExistingTag, item] }
    }, { usesNewTag: [], usesExistingTag: [] })
}


// WEBPACK FOOTER //
// ./src/util/google_calendar_config_utils.js